<template>
    <div class="wrapper auth-split w-100 h-100">
        <div class="auth-split__photo" aria-hidden="true"></div>
        <div class="auth-split__panel">
        <v-card class="auth-split__card" elevation="0">
            <v-card-title>
                <router-link class="d-flex justify-center align-center text-decoration-none" to="/">
                    <div class="text-black d-flex flex-column align-center">
                        <v-img :src="logo" :width="75" draggable="false"/>
                        <v-img :src="logoText" :width="100" draggable="false"/>
                    </div>
                </router-link>
            </v-card-title>
            <v-card-title>Login to SimpliGPS Fleet &amp; Asset Tracking</v-card-title>
            <v-card-text>
                <v-alert v-if="pendingMessage" type="info" variant="tonal" class="mb-4" closable
                         @click:close="pendingMessage = null">
                    <div class="text-body-2 font-weight-medium">Your account is being set up</div>
                    <div class="text-body-2 mt-1">
                        We're still processing your registration. You'll receive an email when your account is ready to use.
                    </div>
                </v-alert>
                <v-alert v-if="suspendedMessage" type="error" variant="tonal" class="mb-4" closable
                         @click:close="suspendedMessage = null">
                    <div class="text-body-2 font-weight-medium">{{ suspendedTitle }}</div>
                    <div class="text-body-2 mt-1">{{ suspendedMessage }}</div>
                    <div v-if="!/please contact/i.test(suspendedMessage)" class="text-body-2 mt-1">
                        Please contact
                        <a href="mailto:support@simpligps.com" class="text-error font-weight-medium">support@simpligps.com</a>
                        for assistance.
                    </div>
                </v-alert>
                <v-form ref="form" v-model="formData.valid" validate-on="input lazy" @submit.prevent="submit">
                    <v-text-field v-model="formData.email" :rules="[vRules.required,vRules.email]" label="Email"
                                  autofocus type="email"/>
                    <v-text-field v-model="formData.password" :rules="[vRules.required]" label="Password"
                                  type="password"/>

                    <div class="mb-3">
                        <v-btn plain size="small" variant="plain" @click="navigateTo('forgot-password')">Forgot
                            Password?
                        </v-btn>
                        <v-btn plain size="small" variant="plain" @click="navigateTo('signup')">Register
                        </v-btn>
                    </div>

                    <v-btn :disabled="!formData.valid || processing || throttleIn > 0"
                           :loading="processing" block color="primary" type="submit">
                        <template v-if="throttleIn > 0">Try again in {{ throttleIn }}s</template>
                        <template v-else>Login</template>
                    </v-btn>
                </v-form>
            </v-card-text>
        </v-card>
        </div>
    </div>
</template>

<script setup>
import router from "@/router";
import {onBeforeUnmount, reactive, ref} from 'vue'
import {send} from '@/composables/apiClient'
import {showApiError} from '@/composables/useApiError'
import logo from "@/assets/images/logo-1.png"
import {useAuthStore} from '@/stores/authStore'
import {landingRoute, useNavigation} from '@/composables/globals'
import logoText from "@/assets/images/logo-text.png"
import {useSnackbarStore} from '@/stores/snackBarStore'

const {navigateTo} = useNavigation()
const registeredEmail = localStorage.getItem('registered_email');
if (registeredEmail) localStorage.removeItem('registered_email');

const formData = reactive({
    valid: false,
    email: registeredEmail || null,
    password: null,
})
const form = ref(null)
const auth = useAuthStore()
const processing = ref(false)
const pendingMessage = ref(null)
const suspendedMessage = ref(null)
const suspendedTitle = ref('Account Suspended')
const snackbar = useSnackbarStore()
const throttleIn = ref(0)
let throttleTimer = null

const startThrottleCooldown = (seconds) => {
    const clamped = Math.max(1, Math.min(600, Math.floor(seconds || 60)))
    throttleIn.value = clamped
    if (throttleTimer) clearInterval(throttleTimer)
    throttleTimer = setInterval(() => {
        throttleIn.value--
        if (throttleIn.value <= 0) clearInterval(throttleTimer)
    }, 1000)
}

onBeforeUnmount(() => { if (throttleTimer) clearInterval(throttleTimer) })

// Only a path inside this app: never another site ("//x", "https:"), so a
// crafted login link cannot bounce a fresh session somewhere else.
const safeRedirect = (value) => {
    const path = Array.isArray(value) ? value[0] : value
    if (typeof path !== 'string' || !path.startsWith('/') || path.startsWith('//')
        || path.includes('\\') || /^\/[a-z][a-z0-9+.-]*:/i.test(path) || path.startsWith('/login')) {
        return null
    }

    return path
}

const submit = () => {
    processing.value = true;
    send('post', 'login', formData, {
        headers: {'X-Client-Source': 'web'},
    }).then((response) => {
        if (response.data.success) {
            if (response.data.data.user.email_verified_at === null) {
                return navigateTo('verify-email', {id: response.data.data.user.id})
            } else if (response.data.data.force_password_change) {
                auth.login(response.data.data)
                return router.push({name: 'force-change-password'})
            } else {
                auth.login(response.data.data)
                // Back to the page that sent them here (an email link, an
                // expired session), which the router passes as ?redirect.
                const back = safeRedirect(router.currentRoute.value.query.redirect)
                return back ? router.push(back) : navigateTo(landingRoute(auth))
            }
        } else {
            snackbar.showSnackbar(response.data.message ?? 'Invalid Credentials', 'error')
        }
    }).catch((error) => {
        const msg = error.response?.data?.message ?? error?.data?.message
        if (error.response?.status === 403 && msg?.toLowerCase().includes('being set up')) {
            pendingMessage.value = msg
            suspendedMessage.value = null
        } else if (error.response?.status === 403 && /suspended|closed/.test(msg?.toLowerCase() ?? '')) {
            suspendedTitle.value = msg.toLowerCase().includes('closed') ? 'Account Closed' : 'Account Suspended'
            suspendedMessage.value = msg
            pendingMessage.value = null
        } else {
            pendingMessage.value = null
            suspendedMessage.value = null
            // Delegate to the shared throttle-aware helper so a 429 becomes
            // "Try again in N minutes" and we get the retryAfterSec back
            // to drive the local button-disable cooldown.
            const info = showApiError(snackbar, error, 'Invalid Credentials')
            if (info.isThrottled) {
                startThrottleCooldown(info.retryAfterSec || 60)
            }
        }
    }).finally(() => processing.value = false)
}
</script>

<style scoped>
.wrapper {
    height: 100%;
}
</style>
